/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable react/no-array-index-key */
'use client';
/* eslint-disable tailwindcss/no-arbitrary-value, tailwindcss/no-unnecessary-arbitrary-value -- CMS-1623 follows fixed Figma modal dimensions and brand colors. */

import { useCalculatedAttributeResolver } from '@hyphengroup/cms-components/calculatedAttributes/useCalculatedAttributeResolver';
import { SingSaverRewardsSectionId } from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/constants';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { ProductBlockAsideModal } from '@hyphengroup/cms-components/components/ProductBlockAsideModal';
import Tooltip from '@hyphengroup/cms-components/components/Tooltip';
import { BaseAttribute } from '@hyphengroup/cms-components/types/attributes';
import { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import { VerticalType } from '@hyphengroup/cms-components/types/cmsTypes';
import { formatAttributeItemValue } from '@hyphengroup/cms-components/utils/helpers';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { trackApplyV3ProductCardDetails } from '../../lib/tracking';
import { getProductVertical } from '../../utils';
import { getDisplayAttributeByVertical } from './PriorityAttribute';
import { getProductPromoCode, PromoCodeRow } from './PromoCodeRow';
import { ApplyStaticText } from '$/(cms)/-cms/apply-v3/[[...slugs]]/i18n';
import { CmsApplyRewardProduct } from '$/(cms)/_modules/cmsService/types/applyRewardProduct';
import { feApiFrontClient } from '$/(cms)/_modules/feApiClient/feApiFrontClient';
import { useProductDetailModal } from '$/components/cms/helpers/useProductDetailModal';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

const getAdditionalInfo = (attr: BaseAttribute): string | undefined => {
  if (
    attr.__typename === 'ComponentAttributesAttrNumber' ||
    attr.__typename === 'ComponentAttributesAttrNumberRange'
  ) {
    return attr.additional_information;
  }
  return undefined;
};

export function ProductCard(props: {
  product: CmsProduct;
  trackingProduct: CmsApplyRewardProduct;
  // True when the card renders inside the apply-v3 modal (M1); false/undefined
  // on the standalone page (M2/M3). The modal is a compact summary: it hides
  // the "See Full Details" link and tightens the promo row spacing.
  isModal?: boolean;
}) {
  const context = useClientContext();
  const staticText = getClientTranslations(ApplyStaticText, context);

  const { product, isModal } = props;
  const resolvedVertical: VerticalType = getProductVertical(
    product.title || '',
  );

  const title = product.product_name ?? product.title ?? '';

  const displayAttributes = getDisplayAttributeByVertical(
    product,
    resolvedVertical,
    context.brand,
  );

  const { openProductDetail, productDetailModal } = useProductDetailModal({
    componentUniqueKey: `ApplyV3ProductCard_${product.uid ?? product.title}`,
  });

  // Asked before the catalogue value is required: a loan rate has no authored
  // value to fall back to (CMS-1959).
  const resolveCalculated = useCalculatedAttributeResolver();

  // With a promo code the row is the visual bottom of the column (its own
  // top padding closes the card); without one keep the original py-2 shape.
  const promoCode = getProductPromoCode(product);

  return (
    <article
      data-component="ApplyV3ProductCard"
      data-product-title={product.title}
      className="overflow-hidden rounded-[10px] border border-borders/default bg-white md:rounded-2xl"
    >
      <div className="flex items-center gap-spacing/gap/x px-spacing/gap/x py-spacing/gap/y-sm md:gap-5 md:px-5 md:py-4">
        <div className="flex h-20 w-[80px] shrink-0 items-center justify-center rounded bg-gray-50 md:h-22 md:w-[88px]">
          {product.product_image ? (
            <CmsImage
              alt={title}
              className="max-h-full max-w-full object-contain"
              height={88}
              sizes={{ _: '80px', sm: '88px' }}
              src={product.product_image}
              width={88}
            />
          ) : (
            <span className="px-2 text-center text-gray-500 text-xs">
              No image
            </span>
          )}
        </div>
        <div
          className={classNames(
            'flex w-full min-w-0 flex-col gap-spacing/gap/soft-8px pt-2',
            { 'pb-2': !promoCode },
          )}
        >
          <h2 className="mhg-alpha-title-1 font-bold text-lg leading-6 md:text-2xl md:leading-8">
            {title}
          </h2>
          <div className="flex flex-col gap-spacing/gap/soft-4px">
            {displayAttributes &&
              displayAttributes.map((attr, index) => {
                const additionalInfo = getAdditionalInfo(attr);
                const calculated = resolveCalculated(attr.name?.name, product);
                // `formatted` is every provider's printable string, so the row
                // never narrows to a provider. Anything the engine does not
                // resolve falls through to the attribute, which already prints
                // its own `empty_value_text` when it carries no value.
                const value =
                  calculated?.state === 'resolved'
                    ? calculated.value.formatted
                    : formatAttributeItemValue(attr, true, true, true);
                return (
                  <div
                    key={index}
                    className="flex justify-between gap-spacing/gap/x"
                  >
                    <div className="mhg-alpha-typography-body-2 min-w-0 text-text/secondary-lighter">
                      {attr.label}
                    </div>
                    {/* The value never breaks — "2.88" and "%" on two lines at
                        375px (CMS-1959 QA); a long label wraps instead. */}
                    <div className="mhg-alpha-bullet-1 flex shrink-0 items-center gap-1 whitespace-nowrap text-end">
                      {calculated?.state === 'pending' ? (
                        <span
                          aria-hidden
                          data-component="ApplyV3ProductCardAttributeLoading"
                          className="h-[15px] w-16 animate-pulse rounded-radius/button bg-fill/alternative-3"
                        />
                      ) : (
                        value
                      )}
                      {additionalInfo && (
                        <Tooltip
                          content={additionalInfo}
                          tooltipClass="z-[var(--z-index-modal-tooltip)] max-w-[300px]"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
          <PromoCodeRow product={product} isModal={isModal} />
        </div>
      </div>
      {!isModal && (
        <>
          <button
            type="button"
            className="flex min-h-11 w-full items-center gap-2 border-t border-gray-300 px-4 font-semibold text-gray-600 text-sm md:min-h-14 md:text-base"
            onClick={() => {
              trackApplyV3ProductCardDetails(props.trackingProduct);
              openProductDetail(product, SingSaverRewardsSectionId);
            }}
          >
            <MaterialIcon icon="article" px={24} />
            {staticText.seeFullDetails}
          </button>
          <ProductBlockAsideModal
            vertical={resolvedVertical}
            tableConfig={undefined}
            pushDataLayer={pushDataLayer}
            getProductAction={(pageUrl, isDraft) =>
              feApiFrontClient.getProduct({
                isDraft,
                productUrl: pageUrl,
                lang: context.lang,
              })
            }
          />
          {productDetailModal}
        </>
      )}
    </article>
  );
}
